import { FC } from "react";
import { Badge } from "@capacit-com/lara-law-shared-ui";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { X, Divide, Search } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Configurable search tag component with support for inclusion/exclusion tags.
 * Features color coding: red/destructive for exclusion tags (-term), primary/default for normal tags.
 * If onRemove is not provided, the X button is hidden (read-only mode).
 */
interface SearchTagProps {
  tag: string;
  onRemove?: () => void;
  variant?: "default" | "inline";
}

export const SearchTag: FC<SearchTagProps> = ({
  tag,
  onRemove,
  variant = "default",
}) => {
  const isExclusion = tag.startsWith("-");
  const displayText = tag.replace(/[-"]/g, "");

  if (variant === "inline") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs shrink-0",
          isExclusion
            ? "bg-destructive text-white"
            : "bg-primary text-primary-foreground"
        )}
      >
        {isExclusion ? (
          <Divide className="size-2.5" />
        ) : (
          <Search className="size-2.5" />
        )}
        <span>{displayText}</span>
        {onRemove && (
          <X
            className="size-2.5 cursor-pointer hover:opacity-70"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
          />
        )}
      </span>
    );
  }

  return (
    <Badge
      variant={isExclusion ? "destructive" : "default"}
      className="rounded-sm pl-2 pr-1 text-sm flex items-center gap-1"
    >
      <div className="flex items-center gap-1 text-xs">
        {isExclusion ? (
          <Divide className="size-2.5" />
        ) : (
          <Search className="size-2.5" />
        )}
        {displayText}
      </div>
      {onRemove && (
        <Button
          variant="ghost"
          size="icon"
          className="size-4.5 cursor-pointer"
          onClick={onRemove}
        >
          <X className="size-2.5" />
        </Button>
      )}
    </Badge>
  );
};
